/**
 * BanksTab Component
 *
 * Renders the Banks tab content for CompanyDetails:
 * - Card listing the company's bank connections (name, bank code, BIC, location)
 * - Info banner pointing to support for credit and risk scores
 *
 * CALCULATION OWNERSHIP: This component receives all data from parent.
 * Loading, error and empty states are handled by the parent and the tab bar
 * (the tab is disabled when the list is empty).
 */

import PropTypes from 'prop-types';
import { ICON_BUTTON_ICONS } from '../../../buttons/IconButton';
import { PUNCTUATION } from '../../../../utils/constants/notation';
import { FIELD_SOURCE, dataSourceClass } from '../../../../utils/constants/dataSources';
import { SUPPORT_EMAIL } from '../../../../utils/constants/subscription';
import { formatBankLocation } from './bankUtils';

const valueOrEmpty = (value) => value || PUNCTUATION.EMPTY_VALUE;

/** Pairs each bank with a React key that stays unique when the source repeats a row. */
const withUniqueKeys = (banks) => {
  const seen = {};
  return banks.map((bank) => {
    const base = [bank.bank_code, bank.bic, bank.bank_name, bank.city].join('|');
    seen[base] = (seen[base] || 0) + 1;
    return { bank, key: `${base}#${seen[base]}` };
  });
};

/**
 * @param {Object} props
 * @param {Array} props.banks - Bank connections
 *   [{bank_name, bank_code, bic, postcode, city, country_name}], sorted by the backend
 */
const BanksTab = ({ banks = [] }) => (
  <div className="banks-tab">
    <div className="card">
      <div className={`heading ${dataSourceClass(FIELD_SOURCE.banks)}`}>Banks</div>
      <div className="banks-header">
        <div className="item">Bank name</div>
        <div className="item">Bank code (BLZ)</div>
        <div className="item">BIC</div>
        <div className="item">Location</div>
      </div>
      <div className="banks-list">
        {withUniqueKeys(banks || []).map(({ bank, key }) => (
          <div className="banks-row" key={key}>
            <div className="item name">{valueOrEmpty(bank.bank_name)}</div>
            <div className="item code">
              <span className="label">BLZ </span>
              {valueOrEmpty(bank.bank_code)}
            </div>
            <div className="item bic">
              <span className="label">BIC </span>
              {valueOrEmpty(bank.bic)}
            </div>
            <div className="item location">{formatBankLocation(bank)}</div>
          </div>
        ))}
      </div>
    </div>

    <div className="risk-note">
      <div className="icon-wrap">
        <img src={ICON_BUTTON_ICONS.EXCLAMATAION_CIRCLE} alt="" />
      </div>
      <div className="content">
        <div className="title">Check creditworthiness and spot business risks</div>
        <div className="text">
          Want to judge this business partner better and see risks early? Credit and risk scores
          give you the transparency you need. Contact us at{' '}
          <a href={`mailto:${SUPPORT_EMAIL}?subject=Risk%20scoring`}>{SUPPORT_EMAIL}</a> for
          details.
        </div>
      </div>
    </div>
  </div>
);

BanksTab.propTypes = {
  banks: PropTypes.arrayOf(
    PropTypes.shape({
      bank_name: PropTypes.string,
      bank_code: PropTypes.string,
      bic: PropTypes.string,
      postcode: PropTypes.string,
      city: PropTypes.string,
      country_name: PropTypes.string
    })
  )
};

export default BanksTab;
